import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scissors, Heart, Star, Camera, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function VisionBoardGuide() {
  const navigate = useNavigate();

  const categories = [
    {
      title: "Health & Wellness",
      ideas: ["Images of women hiking, doing yoga, or radiating vitality", "Healthy meals and vibrant produce", "Spa and self-care imagery", "Words: 'strong,' 'energized,' 'glowing'"],
      color: "green"
    },
    {
      title: "Career & Purpose",
      ideas: ["Your dream workspace or office", "Women leading, speaking, creating", "Symbols of success meaningful to YOU", "Words: 'confident,' 'impactful,' 'leader'"],
      color: "blue"
    },
    {
      title: "Relationships",
      ideas: ["Happy gatherings with friends/family", "Couples enjoying adventures together", "Meaningful conversations over coffee", "Words: 'connected,' 'loved,' 'supported'"],
      color: "pink"
    },
    {
      title: "Personal Growth",
      ideas: ["Books, journals, learning environments", "Travel destinations on your bucket list", "New skills you want to develop", "Words: 'growing,' 'learning,' 'evolving'"],
      color: "purple"
    },
    {
      title: "Home & Environment",
      ideas: ["Your dream living space aesthetic", "Organized, peaceful rooms", "Gardens, outdoor spaces", "Words: 'sanctuary,' 'peaceful,' 'beautiful'"],
      color: "amber"
    },
    {
      title: "Joy & Adventure",
      ideas: ["Activities that make your heart sing", "Places you want to explore", "Hobbies you want to pursue", "Words: 'adventure,' 'joy,' 'freedom'"],
      color: "rose"
    }
  ];

  const steps = [
    {
      step: 1,
      title: "Gather Your Materials",
      items: ["Poster board or cork board (16x20 or larger)", "Magazines (lifestyle, travel, wellness, home)", "Scissors and glue stick or pins", "Printed images from Pinterest/internet", "Markers or colored pens", "Optional: stickers, washi tape, fabric swatches"]
    },
    {
      step: 2,
      title: "Set the Mood",
      items: ["Put on inspiring music", "Light a candle", "Pour your favorite beverage", "Give yourself at least 1-2 hours uninterrupted", "Approach this with curiosity, not perfection"]
    },
    {
      step: 3,
      title: "Collect Without Judging",
      items: ["Flip through magazines and tear out ANYTHING that speaks to you", "Don't analyze yet—just collect what draws you in", "Include images, words, phrases, colors, textures", "Aim for 30-50 pieces to start with"]
    },
    {
      step: 4,
      title: "Curate & Arrange",
      items: ["Spread everything out and look for themes", "Select your favorites (you won't use everything)", "Arrange on your board before gluing", "Consider grouping by category or creating a flowing collage", "Leave some white space—it's not about cramming everything in"]
    },
    {
      step: 5,
      title: "Display Prominently",
      items: ["Place it where you'll see it daily (bedroom, office, closet)", "Take a photo for your phone wallpaper", "Spend a few moments each morning looking at it", "Let it inspire your daily choices and actions"]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-4 text-secondary-700 hover:text-primary"
          >
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-semibold mb-4">
            Week 1 • Day 5 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Creating Your Vision Board
          </h1>
          <p className="text-xl text-secondary-600">
            A Visual Roadmap for Your Next Chapter
          </p>
        </div>

        {/* Hero Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl mb-8">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b182c6e0-1353-47f6-8a97-d98fdf4ac183/images/667fb6fc099616d37999fdddfb77d5c31a977f327ba98c35274836e6158ddeee.png"
            alt="Vision Board Creation"
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Introduction */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
            Why Vision Boards Work
          </h2>
          <p className="text-secondary-700 mb-4">
            Vision boards aren't just arts and crafts—they're a powerful psychological tool. When you visualize your goals, you activate the same neural networks that fire when you actually achieve them. This is called <strong>mental rehearsal</strong>, and elite athletes, CEOs, and performers have used it for decades.
          </p>
          <p className="text-secondary-700 mb-4">
            In your 40s, you have the beautiful advantage of knowing yourself better than ever. You've learned what truly matters, what doesn't serve you, and what kind of life you want to build. A vision board captures this wisdom visually.
          </p>
          <div className="bg-pink-50 rounded-xl p-4 border border-pink-200">
            <p className="text-secondary-800 font-medium flex items-start gap-2">
              <Heart className="w-5 h-5 text-pink-500 mt-1 flex-shrink-0" />
              Your vision board should feel exciting and inspiring when you look at it—not overwhelming or guilt-inducing. If something feels like a "should" rather than a "want," leave it off.
            </p>
          </div>
        </section>

        {/* Categories */}
        <section className="mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 text-center">
            Vision Board Ideas by Category
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {categories.map((category, index) => (
              <div key={index} className={`bg-white rounded-xl p-6 shadow-md border-2 border-${category.color}-200`}>
                <h3 className={`font-bold text-${category.color}-700 mb-3 flex items-center gap-2`}>
                  <Star className={`w-5 h-5 text-${category.color}-500`} />
                  {category.title}
                </h3>
                <ul className="space-y-2">
                  {category.ideas.map((idea, i) => (
                    <li key={i} className="text-sm text-secondary-600 flex items-start gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full bg-${category.color}-400 mt-2 flex-shrink-0`}></span>
                      {idea}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Step by Step */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">
            How to Create Your Vision Board
          </h2>
          <div className="space-y-8">
            {steps.map((item, index) => (
              <div key={index}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center text-white font-bold">
                    {item.step}
                  </span>
                  <h3 className="text-xl font-bold text-secondary-900">{item.title}</h3>
                </div>
                <ul className="grid md:grid-cols-2 gap-2 ml-13">
                  {item.items.map((point, i) => (
                    <li key={i} className="text-secondary-700 flex items-start gap-2 text-sm">
                      <Sparkles className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Digital Option */}
        <section className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-8 border-2 border-pink-200 mb-6">
          <div className="flex items-start gap-4">
            <Camera className="w-10 h-10 text-pink-500 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-serif font-bold text-secondary-900 mb-2">
                Prefer Digital?
              </h2>
              <p className="text-secondary-700 mb-4">
                You can create a digital vision board using Pinterest, Canva, or even your phone's photo app. The key is that you'll see it regularly. Consider:
              </p>
              <ul className="space-y-2 text-sm text-secondary-600">
                <li>• Creating a private Pinterest board with your vision images</li>
                <li>• Making a Canva collage to use as your phone/computer wallpaper</li>
                <li>• Starting a dedicated Instagram saved collection</li>
                <li>• Using the Photos app to create a vision board album</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Prompts */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 text-center">
            Questions to Guide Your Vision
          </h2>
          <p className="text-secondary-600 text-center mb-6">
            Before you start cutting and pasting, reflect on these prompts:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "What does your ideal morning look like?",
              "Where do you see yourself in 5 years?",
              "What activities make you lose track of time?",
              "What would you do if money weren't a concern?",
              "What qualities do you want to embody?",
              "What experiences are on your bucket list?",
              "What does 'success' mean to you now?",
              "What kind of legacy do you want to leave?"
            ].map((question, index) => (
              <div key={index} className="bg-pink-50 rounded-lg p-4">
                <p className="text-secondary-700 text-sm">{question}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default VisionBoardGuide;
